/**
 * Pure field helpers for the sales-order form's section UI.
 *
 * Small status/shape helpers the order form and its section components share:
 * which fields a given order status leaves editable, whether the chosen
 * shipping type needs an address at all, whether an order's billing already
 * matches its shipping, and how to shape a contact object for the address
 * display card. Returns plain JS — no JSX.
 *
 * Contains:
 * - isNoShippingRequired — does the selected shipping type skip an address.
 * - isDisabledForStatus — is a field read-only for the current status.
 * - isBillingSameAsShipping — do an order's billing fields match shipping.
 * - buildContactFromValues — form values -> AddressDisplayCard contact.
 */

// Helper to determine if shipping is required
export const isNoShippingRequired = (shippingTypes, ship_type) => {
  const type = shippingTypes.find(t => t.id == ship_type);
  return type && !type.requires_address;
};

export const isDisabledForStatus = (status, field) => {
  const editableFieldsByStatus = {
    'Billing - On Hold': [
      'site_group',
      'iccid',
      'price',
      'qty_order',
      'qty_fill',
      'salesperson',
      'po_num',
      'comments',
      'hidden_comments',
      'tracking',
      'ship_type',
      'shipping_address',
      'billing_address',
      'ship_instructions',
      'shipping_cost',
      'date_req_by',
      'billing_information',
    ],
    Shipped: [
      'site_group',
      'salesperson',
      'po_num',
      'comments',
      'hidden_comments',
      'is_invoiced',
      'billing_information',
      'ship_type',
      'shipping_cost',
    ],
    Complete: ['salesperson', 'comments', 'hidden_comments'],
  };

  if (status != 'Draft' && (field == 'customer' || field == 'site_group')) {
    return true;
  }

  // In "New" or "Draft", all fields are editable
  if (status === 'New' || status === 'Draft') {
    return false;
  }

  if (editableFieldsByStatus[status]) {
    return !editableFieldsByStatus[status].includes(field);
  }

  // If the status is "Cancelled" or not covered by the above, all fields are disabled
  return true;
};

/**
 * Determine if billing and shipping data are the same
 * Used to auto-check "Same as Shipping" checkbox when loading existing orders
 */
export const isBillingSameAsShipping = values => {
  // Need shipping data to compare
  if (!values.ship_contact && !values.ship_address) {
    return false;
  }
  return (
    values.bill_contact === values.ship_contact &&
    values.bill_email === values.ship_email &&
    values.bill_phone === values.ship_phone &&
    values.bill_address === values.ship_address &&
    values.bill_city === values.ship_city &&
    values.bill_state === values.ship_state &&
    values.bill_zip === values.ship_zip &&
    values.bill_country === values.ship_country
  );
};

/**
 * Build a contact object from Formik values for AddressDisplayCard
 */
export const buildContactFromValues = (values, type) => {
  const prefix = type === 'shipping' ? 'ship' : 'bill';
  const name = values[`${prefix}_contact`];
  const businessName = values[`${prefix}_company_name`];
  const address = values[`${prefix}_address`];
  const city = values[`${prefix}_city`];
  const state = values[`${prefix}_state`];
  const zipcode = values[`${prefix}_zip`];
  const country = values[`${prefix}_country`];
  const email = values[`${prefix}_email`];
  const phone1 = values[`${prefix}_phone`];

  // Return null if no meaningful data
  if (!name && !businessName && !email && !phone1 && !address) {
    return null;
  }

  return {
    name,
    business_name: businessName,
    address,
    city,
    state,
    zipcode,
    country,
    email,
    phone1,
  };
};
